import { cn } from "@/lib/utils";
import { ScheduledJob } from "@/types/common.types";
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { ErrorMessage } from "../../common/Error";
import { ShimmerLoader } from "../../common/ShimmerLoader";
import {
  ShimmerTable,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeader,
  StyledTableRow,
} from "../../common/Table";
import Text from "../../common/Text";

import { Button } from "../../ui/button";
import { ArrowRightIcon } from "lucide-react";

import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import {
  useGetInsightsForJobsQuery,
  InsightsResponse,
  useGetRolesQuery,
} from "@/hooks/queries";

type JobsDataTableProps = {
  job: ScheduledJob;
  onDateClick: (date: string) => void;
};

const InsightDetailForJobTable: React.FC<JobsDataTableProps> = ({
  job,
  onDateClick,
}) => {
  const { sendEvent } = useAnalyticsManager();
  const { data, isLoading, isFetching, isError, error } =
    useGetInsightsForJobsQuery({
      jobId: job.id.toString(),
    });
  const { data: roleData } = useGetRolesQuery();
  const Badge = ({
    children,
    className,
  }: {
    children: React.ReactNode;
    className?: string;
  }) => (
    <span
      className={cn(
        "inline-block rounded-none px-2 py-1 text-xs leading-[14px]",
        className
      )}
    >
      {children}
    </span>
  );

  const insightDataColumns: ColumnDef<InsightsResponse>[] = useMemo(() => {
    const baseColumns: ColumnDef<InsightsResponse>[] = [
      {
        accessorFn: (row) => row.startDate,
        header: "Insights Start Date",
        cell: ({ row }) => (
          <div className="flex flex-col items-start">
            <div className="cursor-pointer text-brand-accent underline">
              {row.original.startDate}
            </div>
          </div>
        ),
      },
      {
        accessorFn: (row) => row.endDate,
        header: "Insights End Date",
        cell: ({ row }) => (
          <div className="flex flex-col items-start">
            <div className="cursor-pointer text-brand-accent underline">
              {row.original.endDate}
            </div>
          </div>
        ),
      },
      {
        id: "actions",
        header: "Action",
        cell: ({ row }) => {
          return (
            <div className="flex items-center justify-start">
              <Button
                variant="outline"
                rightIcon={<ArrowRightIcon size={14} />}
                className="h-8 gap-1 border border-brand-accent px-3 text-[11px] text-brand-accent hover:bg-brand-accent hover:text-white"
                onClick={() => onDateClick(row.original.insightId)}
              >
                View
              </Button>
            </div>
          );
        },
      },
    ];

    if (roleData?.isSuperAdmin) {
      // Insert the tenant name column before the actions column
      baseColumns.splice(baseColumns.length - 1, 0, {
        accessorFn: (row) => row.tenantName,
        header: "Tenant Name",
        cell: ({ row }) => (
          <Badge className="bg-brand-surface text-brand-muted">
            {row.original.tenantName}
          </Badge>
        ),
      });
    }

    return baseColumns;
  }, [roleData?.isSuperAdmin, onDateClick]);

  const table = useReactTable<InsightsResponse>({
    data: data || [],
    columns: insightDataColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const renderCellContent = (cell: Cell<InsightsResponse, unknown>) => {
    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
    return content !== null && content !== undefined ? (
      content
    ) : (
      <ShimmerLoader height={16} width={16} />
    );
  };

  if (isLoading || isFetching) {
    return (
      <JobDataHeader totalJobs={0}>
        <ShimmerTable<InsightsResponse>
          columns={insightDataColumns}
          rowCount={10}
        />
      </JobDataHeader>
    );
  }

  if (isError) {
    return <ErrorMessage message={error?.message} />;
  }

  return (
    <>
      <JobDataHeader totalJobs={data?.length || 0} table={table}>
        <StyledTable>
          <StyledTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <StyledTableRow key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <StyledTableHead
                    key={header.id}
                    className={cn("whitespace-nowrap", {
                      "px-4":
                        index === 0 || index === headerGroup.headers.length - 1,
                    })}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </StyledTableHead>
                ))}
              </StyledTableRow>
            ))}
          </StyledTableHeader>
          <StyledTableBody>
            {table.getRowModel().rows?.length ? (
              <>
                {table.getRowModel().rows.map((row) => (
                  <StyledTableRow
                    key={row.id}
                    className="text-left align-middle"
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <StyledTableCell
                        key={cell.id}
                        className={cn("cursor-pointer", {
                          "px-4":
                            index === 0 ||
                            index === row.getVisibleCells().length - 1,
                        })}
                      >
                        {renderCellContent(cell)}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                ))}
              </>
            ) : (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-left"
                >
                  No results.
                </StyledTableCell>
              </StyledTableRow>
            )}
          </StyledTableBody>
        </StyledTable>
      </JobDataHeader>
    </>
  );
};

export default InsightDetailForJobTable;

type JobDataHeaderProps = {
  totalJobs?: number;
  children: React.ReactNode;
  table?: ReturnType<typeof useReactTable<InsightsResponse>>;
};

const JobDataHeader: React.FC<JobDataHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs && totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
